import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { API } from '../config/api';



export default function PerksTermsPage() {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await fetch(`${API}/perks/terms`);
      if (response.ok) {
        setTerms(await response.json());
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="perks-terms-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/perks">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Perks
          </Button>
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Listener Perks Program Terms</h1>
            {terms && (
              <p className="text-sm text-muted-foreground">
                Version {terms.version} • Effective {terms.effective_date}
              </p>
            )}
          </div>
        </div>

        {/* Terms Content */}
        <Card className="p-8 bg-card/50 border border-border">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
            </div>
          ) : terms ? (
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <h2 className="text-xl font-bold text-foreground mt-8 mb-4 first:mt-0">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-muted-foreground mb-4 leading-relaxed">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">{children}</ul>
                  ),
                  li: ({ children }) => (
                    <li className="text-muted-foreground">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-foreground font-semibold">{children}</strong>
                  )
                }}
              >
                {terms.terms}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-muted-foreground">Failed to load terms</p>
          )}
        </Card>

        {/* Summary Box */}
        {terms && (
          <Card className="mt-6 p-6 bg-amber-500/10 border border-amber-500/30">
            <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Key Points Summary
            </h3>
            <ul className="space-y-2">
              {terms.summary_points?.map((point, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className="text-amber-400">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
